import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import CardHead from "../CardItem/CardHead";
import CardTitle from "../CardItem/CardTitle";
import ToggleCardView, { CardViewOption } from "../CardItem/ToggleCardView";
import RelevancyToggle from "../CardItem/RelevancyToggle";
import CategoryLabelDropdown from "../CategoryLabelDropdown";
import { getDocumentsRelevancy } from "../CardItem";
import {
	getSummaryDocumentText,
	getFullDocumentText,
} from "../../services/fetch";
import { Label, UserFeedback } from "../../types";
import { useClientAccessContext } from "../../context/ClientAccessContext";
import { useDocuments } from "../../context/DocumentContext";
import { useConfig } from "../../context/ConfigContext";
import { usePanelContext } from "../../context/PanelContext";
import { useChat } from "../../context/ChatContext";
import {
	isTranslatable,
	useTranslatedText,
	useTranslationContext,
} from "../../context/TranslationContext";
import {
	LabelOutputType,
	PossibleLabelValue,
} from "../../context/ConfigContext/types";
import { colorGrayLight } from "../../styles/theme";

const ExpandedDocumentCard: FunctionComponent = () => {
	const {
		handleUserFeedback,
		handleUserFeedbackDelete,
		addDocumentToBasket,
		documents,
		documentsInBasket,
	} = useDocuments();
	const { selectedClient } = useClientAccessContext();
	const { numericLabels, categories } = useConfig();
	const { cardView, toggleCardView } = usePanelContext();
	const { isTranslateOn, toggleTranslate } = useTranslationContext();
	const { isSelectionActive, isDocumentSelected, toggleDocumentSelection } =
		useChat();

	const [content, setContent] = useState<string>("");
	const [contentError, setContentError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const document = useMemo(
		() => documents.find((d) => d.id === cardView.documentId),
		[documents, cardView?.documentId]
	);

	const { text: displayedTitle, loading: titleTranslating } = useTranslatedText(
		document.id,
		document.title
	);
	// Don't translate the fetch-failure fallback message - only real article text.
	const { text: displayedContent, loading: translating } = useTranslatedText(
		document.id,
		contentError ? "" : content
	);

	useEffect(() => {
		const fetchContent = async () => {
			if (!selectedClient.clientName || !document.id) return;
			setLoading(true);
			const response =
				cardView.view === CardViewOption.FULL_ARTICLE
					? await getFullDocumentText(document.id, selectedClient.clientName)
					: await getSummaryDocumentText(
							document.id,
							selectedClient.clientName
					  );

			if (!response || !response.isSuccess) {
				setContentError(true);
				setContent(
					cardView.view === CardViewOption.FULL_ARTICLE
						? "Could not find the article"
						: "Could not find summary of the article"
				);
			} else {
				setContentError(false);
				setContent(response.result);
			}
			setLoading(false);
		};
		fetchContent();
	}, [document.id, selectedClient.clientName, cardView.view]);

	const relevancy = getDocumentsRelevancy(document);
	const relevancyLabel = document.labels.find(
		(i) => i.labelType === LabelOutputType.RELEVANCY
	)!;
	const categoryLabels = document.labels.filter(
		(i) => i.labelType === LabelOutputType.CATEGORY
	);

	const handleFeedbackChange = (
		possibleValue: PossibleLabelValue,
		label: Label,
		value = 1,
		labelType = LabelOutputType.CATEGORY
	) => {
		if (
			labelType === LabelOutputType.CATEGORY &&
			possibleValue.id === label.userFeedback.possibleLabelValueId
		)
			return;

		const feedback: UserFeedback = {
			documentId: document.id,
			labelName: label.labelName,
			userId: null,
			labelValue: possibleValue.value,
			possibleLabelValueId: possibleValue.id,
			value,
		};
		handleUserFeedback(feedback);
	};

	const outerWidth =
		cardView.view === CardViewOption.FULL_ARTICLE ? "1100px" : "780px";
	const leftWidth =
		cardView.view === CardViewOption.FULL_ARTICLE ? "35%" : "50%";
	const rightWidth =
		cardView.view === CardViewOption.FULL_ARTICLE ? "65%" : "50%";

	return (
		<Box
			sx={{
				padding: "12px 16px 16px 16px",
				borderRadius: "12px",
				bgcolor: "white",
				width: outerWidth,
				height: "600px",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<CardHead
				score={relevancy}
				isInBasket={documentsInBasket.some((d) => d.id === document.id)}
				onAddToBasket={() => addDocumentToBasket(document.id)}
				translateOn={isTranslateOn(document.id)}
				onToggleTranslate={
					isTranslatable(document.language)
						? () => toggleTranslate(document.id)
						: undefined
				}
				translateLoading={titleTranslating || translating}
				actionsVariant="buttons"
				showChatSelect={isSelectionActive}
				chatSelected={isDocumentSelected(document.id)}
				onToggleChatSelect={() =>
					toggleDocumentSelection({ id: document.id, title: document.title })
				}
			/>
			<Box sx={{ mt: "5px", display: "flex", gap: "40px", height: "100%" }}>
				<Box
					sx={{
						width: leftWidth,
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
					}}
				>
					<Box>
						<CardTitle
							title={displayedTitle}
							date={document.rawCreationDatetime}
							link={document.url}
						/>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: "16px",
								borderTop: `0.5px solid ${colorGrayLight}`,
								mt: "16px",
								pt: "16px",
							}}
						>
							<Typography variant="caption">
								Are these categories correct?
							</Typography>
							{categoryLabels.map((label) => (
								<CategoryLabelDropdown
									key={label.labelId}
									labelName={label.labelName}
									options={categories[label.labelName]}
									labelValue={label.userFeedback.labelValue}
									predictionValue={label.prediction.labelValue}
									onChange={(value) =>
										handleFeedbackChange(
											categories[label.labelName].find(
												(p) => p.value === value
											)!,
											label
										)
									}
								/>
							))}
						</Box>
					</Box>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						<Typography variant="caption">Is this article relevant?</Typography>
						<RelevancyToggle
							selectedValue={relevancyLabel.userFeedback.value}
							onChange={(val) => {
								if (val === null) {
									handleUserFeedbackDelete(relevancyLabel.labelId, document.id);
								} else {
									handleFeedbackChange(
										numericLabels["Relevancy"][0],
										relevancyLabel,
										val,
										LabelOutputType.RELEVANCY
									);
								}
							}}
						/>
					</Box>
				</Box>
				<Box
					sx={{
						width: rightWidth,
						height: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
					}}
				>
					<Box>
						{loading || translating ? (
							<LinearProgress />
						) : (
							<Typography
								variant="body2"
								sx={{
									maxHeight: "442px",
									overflowY: "auto",
									overflowX: "hidden",
									whiteSpace: "pre-wrap",
								}}
							>
								{contentError ? content : displayedContent}
							</Typography>
						)}
					</Box>
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<ToggleCardView
							currentView={cardView.view}
							onViewChange={(v) => toggleCardView(document.id, v)}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default ExpandedDocumentCard;
